// src/api/products.js
import { apiJSON, apiUpload, api } from "../client";

/** =========================
 * Upload helper (Frappe)
 * ========================= */
async function uploadFileGetUrl(file, attachTo = null, onProgress = null) {
  if (!file) return null;

  // Ensure file has a name
  const fileName = file.name || file.filename || "upload";
  
  const form = new FormData();
  form.append("file", file, fileName);
  form.append("is_private", "0"); // public
  
  // ✅ Add required fields for Frappe File doctype (required in production)
  // file_name is mandatory - Frappe needs this to create the File record
  form.append("file_name", fileName);
  
  // ✅ Only append doctype/docname if we're attaching to a specific document
  // Don't append empty docname - it causes "Attached To Name must be a string or an integer" error
  if (attachTo?.doctype && attachTo?.name) {
    form.append("doctype", attachTo.doctype);
    form.append("docname", attachTo.name);
  }
  // For standalone files, don't append doctype/docname at all

  // Use api.post directly to support progress callbacks
  // Use relative URL (BASE is empty string, so path is used as-is)
  const url = "/api/method/upload_file";
  const config = {};
  
  if (onProgress) {
    config.onUploadProgress = (progressEvent) => {
      if (progressEvent && typeof progressEvent.loaded === 'number') {
        if (progressEvent.total && progressEvent.total > 0) {
          // Cap at 99% until upload is actually complete
          const percentCompleted = Math.min(99, Math.round((progressEvent.loaded * 100) / progressEvent.total));
          onProgress(percentCompleted, progressEvent.loaded, progressEvent.total);
        } else if (progressEvent.lengthComputable === false && progressEvent.loaded > 0) {
          // Conservative estimate if total is unknown
          const estimatedPercent = Math.min(90, Math.round((progressEvent.loaded / (1024 * 1024)) * 10));
          onProgress(estimatedPercent, progressEvent.loaded, progressEvent.total);
        }
      }
    };
  }

  const res = await api.post(url, form, config);

  const fileUrl =
    res?.data?.message?.file_url ||
    res?.data?.data?.file_url ||
    res?.data?.file_url ||
    res?.data?.message?.data?.file_url ||
    null;

  if (!fileUrl) {
    console.error("❌ Upload response:", res);
    throw new Error("Upload succeeded but no file_url returned.");
  }
  
  // Set to 100% when upload completes
  if (onProgress) {
    onProgress(100);
  }
  
  console.log("✅ File uploaded successfully:", fileUrl);
  return fileUrl;
}

/* =========================
 * CREATE Mortgage Product
 * ========================= */

export async function createMortgageProduct(payload, onProgress = null) {
  console.log("📥 createMortgageProduct received:", payload);

  const data = { ...(payload || {}) };

  let valuationUrl = null;
  let legalUrl = null;

  // Upload valuation file
  if (data.valuationDoc && typeof data.valuationDoc === "object") {
    console.log("⬆️ Uploading valuation doc:", data.valuationDoc.name);
    const valuationProgress = onProgress?.valuationDoc;
    valuationUrl = await uploadFileGetUrl(data.valuationDoc, null, valuationProgress);
  }

  // Upload legal fees file
  if (data.legalFeesDoc && typeof data.legalFeesDoc === "object") {
    console.log("⬆️ Uploading legal fee doc:", data.legalFeesDoc.name);
    const legalProgress = onProgress?.legalFeesDoc;
    legalUrl = await uploadFileGetUrl(data.legalFeesDoc, null, legalProgress);
  }

  delete data.valuationDoc;
  delete data.legalFeesDoc;

  // Map to backend fields
  if (valuationUrl) {
    data.valuation_fee_spreadsheet = valuationUrl;
  }

  if (legalUrl) {
    data.leagal_fees_spreadsheet = legalUrl; // ✔ correct backend fieldname
  }

  console.log("📤 Sending CREATE payload:", data);

  const endpoint = "/api/resource/" + encodeURIComponent("Mortgage Product");

  const res = await apiJSON(endpoint, {
    method: "POST",
    data,
  });

  console.log("✅ CREATE response:", res);
  return res;
}

/* =========================
 * UPDATE Mortgage Product
 * ========================= */

export async function updateMortgageProduct(name, payload) {
  if (!name) throw new Error("Missing document name for update.");

  console.log("📥 updateMortgageProduct received:", name, payload);

  const data = { ...(payload || {}) };

  let valuationUrl = null;
  let legalUrl = null;

  if (data.valuationDoc && typeof data.valuationDoc === "object") {
    console.log("⬆️ Uploading valuation doc (update):", data.valuationDoc.name);
    valuationUrl = await uploadFileGetUrl(data.valuationDoc);
  }

  if (data.legalFeesDoc && typeof data.legalFeesDoc === "object") {
    console.log("⬆️ Uploading legal doc (update):", data.legalFeesDoc.name);
    legalUrl = await uploadFileGetUrl(data.legalFeesDoc);
  }

  delete data.valuationDoc;
  delete data.legalFeesDoc;

  if (valuationUrl) {
    data.valuation_fee_spreadsheet = valuationUrl;
  }

  if (legalUrl) {
    data.leagal_fees_spreadsheet = legalUrl; // ✔ correct backend fieldname
  }

  const endpoint =
    "/api/resource/" +
    encodeURIComponent("Mortgage Product") +
    "/" +
    encodeURIComponent(name);

  console.log("📤 Sending UPDATE payload:", data);

  const res = await apiJSON(endpoint, {
    method: "PUT",
    data,
  });

  console.log("✅ UPDATE response:", res);
  return res;
}

/** Expose internal helpers for debugging */
export const __productsInternals = {
  uploadFileGetUrl,
};
